import {
  BreakpointsApiKey,
  EditorUIApiKey,
  WorkspaceApiKey,
  ZoomModeApiKey,
} from '#packages/apis';
import * as stateManagement from '#packages/stateManagement';
import type {
  MapStateToProps,
  MapDispatchToProps,
  ThunkAction,
} from 'types/redux';
import constants from '#packages/constants';
import * as util from '#packages/util';
import type { CSSProperties, UIEventHandler } from 'react';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
import { DESKTOP_CANVAS_SIZE } from '@/preview';

export interface FixedEditingAreaOwnProps {
  leftBarAreaRef: React.RefObject<HTMLDivElement>;
}
export interface FixedEditingAreaStateProps {
  shouldCutEditingArea: boolean;
  editingAreaWidth: number;
  siteScale: number;
  styleForPushedStageAndPreviewFrame: CSSProperties;
  stageHeight: string;
  isMobileEditor: boolean;
  isDesktopPreview: boolean;
  previewWidth: number;
  isZoomOut: boolean;
  isStageBannerEnabled: boolean;
  contributedStageBannerComponent: React.ComponentType<{}> | undefined;
  shouldRenderMobileWizard: boolean;
  isPreviewMode: boolean;
  shouldAnimatePreviewWidth: boolean;
  isInteractionMode: boolean;
  isTranslationMode: boolean;
  isLightboxMode: boolean;
  baselineWidth: number;
  isStageZoomMode: boolean;
  isZoomMode: boolean;
  helpPanelProps: stateManagement.EditorState['helpProps'];
  selectedComponents: CompRef[];
  isPinMode: boolean;
  performingMouseMoveAction: boolean;
  mobileWizardOpeningState: keyof typeof constants.MOBILE.MOBILE_WIZARD.OPENING_STATE;
  fixedStageWidth: number;
  shouldRenderFixedStageTopGap: boolean;
  isFullScreenPreview: boolean;
}

export interface FixedEditingAreaDispatchProps {
  zoomByMouseWheel: (event: React.WheelEvent<HTMLDivElement>) => Promise<void>;
  refreshEditingAreaPosition: (rect: any) => void;
  registerNewScrollPosition: UIEventHandler<HTMLDivElement>;
}

export interface FixedEditingAreaProps
  extends FixedEditingAreaOwnProps,
    FixedEditingAreaStateProps,
    FixedEditingAreaDispatchProps {}

export const mapStateToProps: MapStateToProps<
  FixedEditingAreaStateProps,
  FixedEditingAreaOwnProps
> = (stateMapperArgs) => {
  const { state: editorState, editorAPI } = stateMapperArgs;

  const isPreviewMode =
    stateManagement.preview.selectors.getPreviewMode(editorState);
  const previewType = isPreviewMode
    ? stateManagement.preview.selectors.getPreviewType(editorState)
    : undefined;
  const siteScale = editorState.siteScale;
  const siteHeight = editorAPI.dsRead?.site.getHeight();

  const stageHeightForZoomedStage =
    !stateManagement.stateMapperArgsSelectors.mouseSels.isDuringMouseActionSel(
      stateMapperArgs,
    ) && siteHeight * siteScale;

  const isDesktopPreview = isPreviewMode && previewType === 'desktop';

  const previewWidth =
    previewType === 'fullScreen'
      ? stateManagement.domMeasurements.selectors.getEditorContentLayout(
          editorState,
        ).width
      : stateManagement.preview.selectors.getPreviewWidth(editorState);

  const shouldAnimatePreviewWidth =
    stateManagement.preview.selectors.getIsAnimatedPreviewResize(editorState) &&
    previewWidth > 0;

  const contributedStageBannerComponent = editorAPI.host
    .getAPI(WorkspaceApiKey)
    .getStageBannerComponent();

  const isMobileEditor = editorAPI.isMobileEditor();

  const isPreviewReady = editorAPI.isPreviewReady();

  const isMobileWizardEnabled =
    stateManagement.mobile.selectors.mobileWizard.getIsMobileWizardEnabled(
      editorState,
    );

  const mobileWizardOpeningState =
    stateManagement.mobile.selectors.mobileWizard.getMobileWizardOpeningState(
      editorState,
    );

  const activeBreakpoint = editorAPI.host
    .getAPI(BreakpointsApiKey)
    .getFocusedPageActiveBreakpoint();

  return {
    shouldCutEditingArea: editorAPI.host
      .getAPI(EditorUIApiKey)
      .stage.shouldCutEditingArea(),
    editingAreaWidth: editorAPI.getEditingAreaPosition().width,
    siteScale,
    styleForPushedStageAndPreviewFrame: editorAPI.host
      .getAPI(ZoomModeApiKey)
      .getStyleForPushedStageAndPreviewFrame(),

    stageHeight:
      siteScale === 1 || !stageHeightForZoomedStage || isNaN(siteHeight)
        ? '100%'
        : `${stageHeightForZoomedStage}px`,
    isMobileEditor,
    isDesktopPreview,
    previewWidth,
    isZoomOut: editorAPI.zoomMode.isInZoomMode(),
    isStageBannerEnabled: contributedStageBannerComponent && !isPreviewMode,
    contributedStageBannerComponent,
    shouldRenderMobileWizard:
      isPreviewReady && isMobileEditor && isMobileWizardEnabled,
    isPreviewMode,
    shouldAnimatePreviewWidth,
    isInteractionMode:
      stateManagement.interactions.selectors.isInInteractionMode(editorState),
    isTranslationMode:
      stateManagement.multilingual.selectors.isTranslating(editorState),
    isLightboxMode: editorAPI.dsRead?.pages.popupPages.isPopupOpened(),
    baselineWidth: editorAPI.site?.getWidth && editorAPI.site.getWidth(),
    mobileWizardOpeningState,
    isStageZoomMode: editorAPI.zoomMode.isStageZoomMode(),
    isZoomMode: editorAPI.zoomMode.isInZoomMode(),
    helpPanelProps: editorState.helpProps,
    selectedComponents:
      stateManagement.selection.selectors.getSelectedCompsRefs(editorState),
    isPinMode: stateManagement.pinMode.selectors.isOpen(editorState),
    performingMouseMoveAction:
      stateManagement.mouseActions.selectors.isPerformingMouseMoveAction(
        editorState,
      ),
    fixedStageWidth: activeBreakpoint?.value.canvasSize || DESKTOP_CANVAS_SIZE,
    shouldRenderFixedStageTopGap: util.fixedStage.shouldRenderFixedTopGap(
      isPreviewMode,
      previewType,
    ),
    isFullScreenPreview: previewType === 'fullScreen',
  };
};

const getEditorAPI: ThunkAction<EditorAPI> = (
  _dispatch,
  _getState,
  { editorAPI },
) => editorAPI;

export const mapDispatchToProps: MapDispatchToProps<
  FixedEditingAreaDispatchProps,
  FixedEditingAreaOwnProps
> = (dispatch) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);
  return {
    zoomByMouseWheel: async (event: React.WheelEvent<HTMLDivElement>) => {
      await editorAPI.zoomMode.zoomByMouseWheel(event);
    },
    refreshEditingAreaPosition: (editingAreaRect) =>
      editorAPI.refreshEditingAreaPosition(editingAreaRect),
    registerNewScrollPosition: (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollLeft } = event.target as HTMLDivElement;
      editorAPI.scroll.registerNewScrollPosition({ scrollTop, scrollLeft });
    },
  };
};
